import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type Access, type ExpenseFilters } from './api';

/**
 * Query keys are structured so anything touching receipts can invalidate
 * `['expenses']` and `['summary']` together — the headline figure and the list
 * must never disagree about what was just saved.
 */
export const keys = {
  session: ['session'] as const,
  me: ['me'] as const,
  ledgers: ['ledgers'] as const,
  ledger: (id: string) => ['ledger', id] as const,
  grants: (id: string) => ['grants', id] as const,
  shareable: (id: string) => ['shareable', id] as const,
  people: (ledgerId: string) => ['people', ledgerId] as const,
  groups: ['groups'] as const,
  group: (id: string) => ['group', id] as const,
  categories: ['categories'] as const,
  expenses: (filters: ExpenseFilters) => ['expenses', filters] as const,
  expense: (id: string) => ['expense', id] as const,
  summary: (filters: ExpenseFilters) => ['summary', filters] as const,
  settings: ['settings'] as const,
  users: ['users'] as const,
};

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: api.session,
    // Fresh on every mount: this decides whether the app renders at all.
    staleTime: 0,
    retry: false,
  });
}

export function useLedgers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.ledgers,
    queryFn: api.ledgers,
    staleTime: 30_000,
    // Signed out this would 401, and the cached failure would still be sitting
    // there the moment somebody signs in — which is how a freshly set-up
    // instance ends up claiming nothing has been shared with you.
    enabled: options?.enabled ?? true,
  });
}

export function useLedger(id: string | undefined) {
  return useQuery({
    queryKey: keys.ledger(id ?? ''),
    queryFn: () => api.ledger(id!),
    enabled: Boolean(id),
  });
}

export function usePeople(ledgerId: string | undefined) {
  return useQuery({
    queryKey: keys.people(ledgerId ?? ''),
    queryFn: () => api.people(ledgerId!),
    enabled: Boolean(ledgerId),
    staleTime: 60_000,
  });
}

export function useGroups() {
  return useQuery({ queryKey: keys.groups, queryFn: api.groups, staleTime: 60_000 });
}

export function useCategories() {
  return useQuery({ queryKey: keys.categories, queryFn: api.categories, staleTime: 300_000 });
}

export function useExpenses(filters: ExpenseFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.expenses(filters),
    queryFn: () => api.expenses(filters),
    // Keeping the previous page on screen while the next loads is what stops a
    // filter change from flashing an empty list.
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export function useExpense(id: string | undefined) {
  return useQuery({
    queryKey: keys.expense(id ?? ''),
    queryFn: () => api.expense(id!),
    enabled: Boolean(id),
    // While Paperless is still consuming a document, poll so the card flips
    // from "Filing" to a thumbnail without the user refreshing.
    //
    // Every second, not every four: Paperless usually finishes a phone photo in
    // about a second, and the server now marks the document viewable before it
    // does the filing work. A four-second poll threw most of that away — the
    // document was ready and the screen had not noticed yet.
    //
    // And it backs off rather than polling forever. A document still filing two
    // minutes later is not about to finish — Paperless is down, or something is
    // wrong — and polling every second until the server writes it off six hours
    // later is how a phone makes thousands of requests across a screen lock and
    // a network change. One of those fails, and a failed request is how this
    // screen came to claim the receipt did not exist.
    //
    // Timed from the document's own createdAt, so it measures how long *it* has
    // been waiting rather than how long ago this query last succeeded.
    refetchInterval: (query) => {
      const pending = (query.state.data?.expense.documents ?? []).filter(
        (doc) => doc.status === 'pending',
      );
      if (pending.length === 0) return false;
      const oldest = Math.min(...pending.map((doc) => Date.parse(doc.createdAt) || Date.now()));
      return Date.now() - oldest > 120_000 ? 30_000 : 1000;
    },
  });
}

export function useSummary(filters: ExpenseFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.summary(filters),
    queryFn: () => api.summary(filters),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: api.settings });
}

export function useUsers() {
  return useQuery({ queryKey: keys.users, queryFn: api.users });
}

/** Invalidates everything a receipt change can affect. */
export function useReceiptInvalidation() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['expenses'] });
    void client.invalidateQueries({ queryKey: ['expense'] });
    void client.invalidateQueries({ queryKey: ['summary'] });
    void client.invalidateQueries({ queryKey: keys.ledgers });
  };
}

/** Invalidates everything a change to who-can-see-what can affect. */
export function useAccessInvalidation() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['ledger'] });
    void client.invalidateQueries({ queryKey: ['grants'] });
    void client.invalidateQueries({ queryKey: ['shareable'] });
    void client.invalidateQueries({ queryKey: keys.ledgers });
    void client.invalidateQueries({ queryKey: keys.groups });
    void client.invalidateQueries({ queryKey: keys.users });
  };
}

export function useCreateExpense() {
  const invalidate = useReceiptInvalidation();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.createExpense(body),
    onSuccess: invalidate,
  });
}

export function useUpdateExpense(id: string) {
  const invalidate = useReceiptInvalidation();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.updateExpense(id, body),
    onSuccess: invalidate,
  });
}

export function useReimburse(id: string) {
  const invalidate = useReceiptInvalidation();
  return useMutation({
    mutationFn: (body: { amountCents?: number; date?: string; note?: string }) =>
      api.reimburse(id, body),
    onSuccess: invalidate,
  });
}

export function useUnreimburse(id: string) {
  const invalidate = useReceiptInvalidation();
  return useMutation({ mutationFn: () => api.unreimburse(id), onSuccess: invalidate });
}

export function useDeleteExpense() {
  const invalidate = useReceiptInvalidation();
  return useMutation({
    mutationFn: ({ id, purgeDocuments }: { id: string; purgeDocuments?: boolean }) =>
      api.deleteExpense(id, purgeDocuments),
    onSuccess: invalidate,
  });
}

/** Accept or decline what Paperless read off a document. */
export function useDecideSuggestion(expenseId: string) {
  const invalidate = useReceiptInvalidation();
  return useMutation({
    mutationFn: ({
      documentId,
      ...body
    }: {
      documentId: string;
      decision: 'apply' | 'dismiss';
      amountCents?: number;
      serviceDate?: string;
      provider?: string | null;
    }) => api.decideSuggestion(expenseId, documentId, body),
    onSuccess: invalidate,
  });
}

export function useCreateLedger() {
  const invalidate = useAccessInvalidation();
  return useMutation({
    mutationFn: api.createLedger,
    onSuccess: invalidate,
  });
}

export function useShare(ledgerId: string) {
  const invalidate = useAccessInvalidation();
  return useMutation({
    mutationFn: (body: { userId?: string; groupId?: string; access: Access }) =>
      api.share(ledgerId, body),
    onSuccess: invalidate,
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.updateMe,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.session });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useInvalidate(key: readonly unknown[]) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: key });
}
